import React, { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { FieldInputProps } from 'react-final-form';
import isPropValid from '@emotion/is-prop-valid';
import classNames from 'classnames';
import styled, { createGlobalStyle, css, keyframes, StyleSheetManager } from 'styled-components';

import { BaseTheme } from './baseTheme';

type SpaceValue = number | string;

type ResponsiveProps = {
  mx?: SpaceValue | SpaceValue[];
  my?: SpaceValue | SpaceValue[];
  mt?: SpaceValue | SpaceValue[];
  mb?: SpaceValue | SpaceValue[];
  px?: SpaceValue | SpaceValue[];
  py?: SpaceValue | SpaceValue[];
};

type GridProps = ResponsiveProps & {
  children?: ReactNode;
  className?: string;
  size?: number[];
  flex?: string;
  flexFlow?: string;
  flexBasis?: string;
  flexGrow?: string;
  flexWrap?: string;
  height?: string;
};

const spaceValue = (value: SpaceValue, theme: typeof BaseTheme) =>
  typeof value === 'number' ? theme.space[value] || `${value}px` : value;

const responsiveCss = (property: string, values: SpaceValue | SpaceValue[] | undefined) => {
  if (values === undefined) {
    return '';
  }
  const items = Array.isArray(values) ? values : [values];
  const breakpoints = [0, ...BaseTheme.breakpoints];

  return items
    .map((value, index) => {
      const declaration = `${property}: ${spaceValue(value, BaseTheme)};`;
      return index === 0 ? declaration : `@media (min-width: ${breakpoints[index]}px) { ${declaration} }`;
    })
    .join('\n');
};

export const Row = styled.div<GridProps>`
  box-sizing: border-box;
  display: flex;
  flex: ${props => props.flex || '0 1 auto'};
  flex-wrap: ${props => props.flexWrap || 'wrap'};
  ${props => props.flexFlow && `flex-flow: ${props.flexFlow};`}
  height: ${props => props.height || 'auto'};
  margin-right: -0.75rem;
  margin-left: -0.75rem;
  ${props => responsiveCss('margin-top', props.mt || props.my)}
  ${props => responsiveCss('margin-bottom', props.mb || props.my)}
  ${props => responsiveCss('margin-left', props.mx)}
  ${props => responsiveCss('margin-right', props.mx)}
`;

export const Column = styled.div<GridProps>`
  box-sizing: border-box;
  flex: ${props => props.flex || '0 0 auto'};
  flex-basis: ${props => props.flexBasis || '0'};
  flex-grow: ${props => props.flexGrow || '1'};
  padding-right: 0.75rem;
  padding-left: 0.75rem;
  ${props =>
    props.size?.map((size, index) => {
      const width = `${(size / BaseTheme.gridSize) * 100}%`;
      return index === 0
        ? `flex-basis: ${width}; max-width: ${width};`
        : `@media (min-width: ${BaseTheme.breakpoints[index - 1]}px) { flex-basis: ${width}; max-width: ${width}; }`;
    })}
  ${props => responsiveCss('margin-top', props.mt || props.my)}
  ${props => responsiveCss('margin-bottom', props.mb || props.my)}
  ${props => responsiveCss('margin-left', props.mx)}
  ${props => responsiveCss('margin-right', props.mx)}
  ${props => responsiveCss('padding-top', props.py)}
  ${props => responsiveCss('padding-bottom', props.py)}
  ${props => responsiveCss('padding-left', props.px)}
  ${props => responsiveCss('padding-right', props.px)}
`;

export const Box = styled.div<GridProps & { noMargin?: boolean }>`
  ${props => !props.noMargin && 'margin-bottom: 0.75rem;'}
  ${props => responsiveCss('margin-top', props.mt || props.my)}
  ${props => responsiveCss('margin-bottom', props.mb || props.my)}
  ${props => responsiveCss('margin-left', props.mx)}
  ${props => responsiveCss('margin-right', props.mx)}
  ${props => responsiveCss('padding-top', props.py)}
  ${props => responsiveCss('padding-bottom', props.py)}
  ${props => responsiveCss('padding-left', props.px)}
  ${props => responsiveCss('padding-right', props.px)}
`;

export type ButtonProps = Omit<ComponentPropsWithoutRef<'button'>, 'type'> & {
  as?: ElementType;
  type?: string | number;
  htmlType?: string;
  icon?: string | React.ComponentType | ReactNode;
  iconAfterText?: boolean;
};

const ButtonBase = styled.button<{ $variant: string | number; $iconOnly: boolean }>`
  align-items: center;
  border: 0;
  border-radius: 1rem;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  font-family: ${props => props.theme.variables.typography.secondaryFont};
  font-size: 1rem;
  justify-content: center;
  min-height: 2rem;
  min-width: ${props => (props.$iconOnly ? '2rem' : '5.5rem')};
  outline: none;
  padding: ${props => (props.$iconOnly ? '0' : `0 ${props.theme.variables.button.horizontalPadding}`)};
  transition:
    color 0.3s,
    background-color 0.3s;

  > *:not(:last-child) {
    margin-right: 0.5rem;
  }

  ${props => (props.theme.ruleSets.button[String(props.$variant)] || props.theme.ruleSets.button[0]).common}
  ${props =>
    (props.theme.ruleSets.button[String(props.$variant)] || props.theme.ruleSets.button[0])[
      props.disabled ? 'disabled' : 'enabled'
    ]}
`;

const ButtonIcon = styled.span<{ $icon: string }>`
  background-image: ${props => {
    const iconUrl = props.theme.variables.icons.svg[props.$icon];
    return iconUrl ? `url("${iconUrl}")` : 'none';
  }};
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  display: inline-block;
  height: 1.5rem;
  width: 1.5rem;
`;

const ButtonComponent = ({
  as,
  children,
  className,
  disabled,
  htmlType,
  icon,
  iconAfterText,
  type,
  ...rest
}: ButtonProps) => {
  const variant = type ?? 0;
  const iconOnly = variant === 2;
  const iconNode =
    typeof icon === 'function' ? (
      React.createElement(icon)
    ) : typeof icon === 'string' ? (
      <ButtonIcon aria-hidden="true" $icon={icon} />
    ) : (
      icon
    );

  return (
    <ButtonBase
      as={as || 'button'}
      $iconOnly={iconOnly}
      $variant={variant}
      className={classNames('button', className)}
      disabled={disabled}
      type={htmlType || 'button'}
      {...rest}
    >
      {iconNode && !iconAfterText && iconNode}
      {children}
      {iconNode && iconAfterText && iconNode}
    </ButtonBase>
  );
};

export const Button = Object.assign(ButtonComponent, {
  types: {
    PRIMARY: 0,
    FLAT: 1,
    ICON: 2,
    NAVIGATION: 3,
  },
});

type FormInput<T extends HTMLInputElement | HTMLTextAreaElement> = FieldInputProps<string, T>;

type FieldMeta = {
  error?: ReactNode;
  touched?: boolean;
  submitError?: ReactNode;
};

type FieldProps<T extends HTMLInputElement | HTMLTextAreaElement> = ResponsiveProps & {
  autoComplete?: ComponentPropsWithoutRef<'input'>['autoComplete'];
  className?: string;
  disabled?: boolean;
  label?: string;
  input: FormInput<T>;
  meta?: FieldMeta;
  charsCounter?: boolean;
  maxLenght?: number;
  clearable?: boolean;
};

const FieldWrapper = styled.div`
  font-family: ${props => props.theme.variables.typography.primaryFont};
  font-weight: ${props => props.theme.variables.fonts.weight.light};
  margin-bottom: 1rem;
  width: 100%;
`;

const FieldContent = styled.div`
  position: relative;
  width: 100%;
`;

const FieldLabel = styled.label<{ $textarea?: boolean }>`
  background: ${props => (props.$textarea ? props.theme.variables.colors.white : 'transparent')};
  color: ${props => props.theme.variables.colors.darkGrey};
  cursor: text;
  font-family: ${props => props.theme.variables.typography.primaryFont};
  font-size: ${props => (props.$textarea ? '1rem' : props.theme.variables.fonts.size.base)};
  left: ${props => (props.$textarea ? '1px' : '0')};
  line-height: 1.25;
  padding: ${props => (props.$textarea ? '0.8125rem 1rem 0 calc(1rem - 1px)' : '0 1rem')};
  pointer-events: none;
  position: absolute;
  top: ${props => (props.$textarea ? '0.0625rem' : '1.1rem')};
  transition:
    font-size 0.15s ease,
    padding-top 0.15s ease;
  ${props => props.$textarea && 'width: calc(100% - 5rem - 1px);'}
`;

const FieldControl = styled.input<{ $hasCounter: boolean; $hasClearButton: boolean }>`
  background: ${props => props.theme.variables.colors.white};
  border: 1px solid ${props => props.theme.variables.colors.platinum};
  border-bottom: 1px solid ${props => props.theme.variables.colors.midGrey};
  border-radius: 0.1875rem;
  box-sizing: border-box;
  color: ${props => props.theme.variables.colors.darkGrey};
  font: 400 1rem ${props => props.theme.variables.typography.primaryFont};
  min-height: 3.5rem;
  padding: 1.25rem
    ${props =>
      props.$hasCounter && props.$hasClearButton
        ? '6.25rem'
        : props.$hasCounter
          ? '4.5rem'
          : props.$hasClearButton
            ? '2.75rem'
            : '1rem'}
    1.25rem 1rem;
  width: 100%;

  &:focus {
    border-bottom: 2px solid ${props => props.theme.variables.colors.blue};
    outline: none;
  }

  &:focus + ${FieldLabel}, &:not(:placeholder-shown) + ${FieldLabel} {
    visibility: hidden;
  }

  &:disabled {
    border-color: transparent;
  }

  &[aria-invalid='true'] {
    border-bottom-color: ${props => props.theme.variables.colors.red};
  }
`;

const FieldError = styled.div`
  color: ${props => props.theme.variables.colors.red};
  font-size: ${props => props.theme.variables.fonts.size.smaller1};
  margin-top: 0.25rem;
`;

const FieldClearButton = styled.button`
  background: transparent;
  border: 0;
  color: ${props => props.theme.variables.colors.darkGrey};
  cursor: pointer;
  font-size: 1.25rem;
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
`;

const FieldCounter = styled.div<{ $textarea: boolean; $hasClearButton: boolean }>`
  align-items: ${props => (props.$textarea ? 'flex-start' : 'center')};
  bottom: ${props => (props.$textarea ? '0' : '1.5rem')};
  color: ${props => props.theme.variables.customColors.lightNavy};
  display: flex;
  font-family: ${props => props.theme.variables.typography.secondaryFont};
  font-size: ${props => props.theme.variables.fonts.size.smaller1};
  font-weight: ${props => props.theme.variables.fonts.weight.bold};
  margin: ${props => (props.$textarea ? '1.125rem 1.125rem 0 0' : '0 1.125rem')};
  pointer-events: none;
  position: absolute;
  right: ${props => (props.$hasClearButton ? '2.5rem' : '0')};
  top: 0;
`;

const FieldCounterMaximum = styled.span`
  color: ${props => props.theme.variables.customColors.lightBlue};
`;

const TextareaControl = styled.textarea<{ $hasCounter: boolean; $resizeable: boolean }>`
  background: ${props => props.theme.variables.colors.white};
  border: 1px solid ${props => props.theme.variables.colors.platinum};
  border-bottom: 1px solid ${props => props.theme.variables.colors.midGrey};
  border-radius: 0.1875rem;
  box-sizing: border-box;
  color: ${props => props.theme.variables.colors.darkGrey};
  font: 300 1rem ${props => props.theme.variables.typography.primaryFont};
  padding: 1.25rem ${props => (props.$hasCounter ? '4.5rem' : '1rem')} 1.25rem 1rem;
  resize: ${props => (props.$resizeable ? 'vertical' : 'none')};
  width: 100%;

  &:focus {
    border-bottom: 2px solid ${props => props.theme.variables.colors.blue};
    outline: none;
  }

  &:focus,
  &:not(:placeholder-shown) {
    padding-top: 1.75rem;
    padding-bottom: 0.75rem;
  }

  &:focus + ${FieldLabel}, &:not(:placeholder-shown) + ${FieldLabel} {
    font-size: ${props => props.theme.variables.fonts.size.smaller1};
    padding-top: 0.375rem;
  }

  &:disabled {
    border-color: transparent;
  }

  &[aria-invalid='true'] {
    border-bottom-color: ${props => props.theme.variables.colors.red};
  }
`;

const getFieldError = (meta?: FieldMeta) => (meta?.touched ? meta.error || meta.submitError : undefined);

export const Input = ({
  autoComplete,
  className,
  clearable,
  disabled,
  input,
  label,
  maxLenght,
  meta,
  charsCounter,
}: FieldProps<HTMLInputElement>) => {
  const error = getFieldError(meta);
  const value = input.value == null ? '' : String(input.value);
  const fieldClassName = classNames('input', className, { 'input--error': Boolean(error) });

  return (
    <FieldWrapper className={fieldClassName}>
      <FieldContent className="input__content">
        <FieldControl
          aria-label={label}
          aria-invalid={Boolean(error)}
          autoComplete={autoComplete}
          className="input__field"
          disabled={disabled}
          id={input.name}
          maxLength={maxLenght}
          name={input.name}
          onBlur={input.onBlur}
          onChange={input.onChange}
          onFocus={input.onFocus}
          placeholder=" "
          $hasCounter={Boolean(charsCounter)}
          $hasClearButton={Boolean(clearable)}
          type="text"
          value={value}
        />
        {label && (
          <FieldLabel className="input__label" htmlFor={input.name}>
            {label}
          </FieldLabel>
        )}
        {clearable && value && (
          <FieldClearButton
            aria-label={`Clear ${label || input.name}`}
            className="input__clear-button"
            onClick={() => input.onChange('')}
            type="button"
          >
            ×
          </FieldClearButton>
        )}
        {charsCounter && (
          <FieldCounter className="input__counter" $textarea={false} $hasClearButton={Boolean(clearable)}>
            <span className="input__chars-number">{value.length}</span>
            {maxLenght && (
              <>
                {' / '}
                <FieldCounterMaximum className="input__max-number">{maxLenght}</FieldCounterMaximum>
              </>
            )}
          </FieldCounter>
        )}
      </FieldContent>
      {error && <FieldError role="alert">{error}</FieldError>}
    </FieldWrapper>
  );
};

export const Textarea = ({
  autoComplete,
  className,
  disabled,
  input,
  label,
  maxLenght,
  meta,
  charsCounter,
  rows = 4,
  resizeable = true,
}: FieldProps<HTMLTextAreaElement> & { rows?: number; resizeable?: boolean }) => {
  const error = getFieldError(meta);
  const value = input.value == null ? '' : String(input.value);
  const fieldClassName = classNames('textarea', className, { 'textarea--error': Boolean(error) });

  return (
    <FieldWrapper className={fieldClassName}>
      <FieldContent className="textarea__content">
        <TextareaControl
          aria-label={label}
          aria-invalid={Boolean(error)}
          autoComplete={autoComplete}
          className="textarea__field"
          disabled={disabled}
          id={input.name}
          maxLength={maxLenght}
          name={input.name}
          onBlur={input.onBlur}
          onChange={input.onChange}
          onFocus={input.onFocus}
          placeholder=" "
          rows={rows}
          $hasCounter={Boolean(charsCounter)}
          $resizeable={resizeable}
          value={value}
        />
        {label && (
          <FieldLabel $textarea={true} className="textarea__label" htmlFor={input.name}>
            {label}
          </FieldLabel>
        )}
        {charsCounter && (
          <FieldCounter className="textarea__counter" $textarea={true} $hasClearButton={false}>
            <span className="textarea__chars-number">{value.length}</span>
            {maxLenght && (
              <>
                {' / '}
                <FieldCounterMaximum className="textarea__max-number">{maxLenght}</FieldCounterMaximum>
              </>
            )}
          </FieldCounter>
        )}
      </FieldContent>
      {error && <FieldError role="alert">{error}</FieldError>}
    </FieldWrapper>
  );
};

export const GlobalStyle = createGlobalStyle`
  html, body, div, span, applet, object, iframe,
  h1, h2, h3, h4, h5, h6, p, blockquote, pre,
  a, abbr, acronym, address, big, cite, code,
  del, dfn, em, img, ins, kbd, q, s, samp,
  small, strike, strong, sub, sup, tt, var,
  b, u, i, center,
  dl, dt, dd, ol, ul, li,
  fieldset, form, label, legend,
  table, caption, tbody, tfoot, thead, tr, th, td,
  article, aside, canvas, details, embed,
  figure, figcaption, footer, header, hgroup,
  main, menu, nav, output, ruby, section, summary,
  time, mark, audio, video {
    margin: 0;
    padding: 0;
    border: 0;
    font-size: 100%;
    font: inherit;
    vertical-align: baseline;
  }

  html {
    font-size: ${props => props.theme.variables.fonts.size.root};
  }

  html, body {
    height: 100%;
    min-height: 100%;
    overflow-x: hidden;
  }

  body {
    color: ${props => props.theme.variables.colors.black};
    font-family: ${props => props.theme.variables.typography.primaryFont};
    font-size: ${props => props.theme.variables.fonts.size.base};
    font-weight: ${props => props.theme.variables.fonts.weight.regular};
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  svg {
    display: block;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }
`;

export const DesignSystemStyleManager = ({ children, nonce }: { children: ReactNode; nonce?: string }) => (
  <StyleSheetManager
    nonce={nonce}
    shouldForwardProp={(prop, target) => typeof target !== 'string' || isPropValid(prop)}
  >
    {children}
  </StyleSheetManager>
);

export { BaseTheme, css, keyframes };
export { NotificationsContextProvider } from './context';
